import { Color, Spacing } from '@persona-id/design-tokens';
import { ListIcon } from '@persona-id/design-tokens/icons';
import { isEqual } from 'lodash';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import { StyledMetricsFilterButton } from 'dashboard/components/VerificationAnalytics';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import Checkbox from 'design-system/components/Checkbox';
import Popover from 'design-system/components/Popover';
import { Paragraph } from 'design-system/components/Text';
import { hoverMixin } from 'design-system/mixins/hoverable';
import { VerificationType } from 'lib/interfaces/api';

const MAX_VERIFICATION_TEMPLATE_FILTER_CHECKBOX_WIDTH = 350;

const StyledCheckbox = styled(Checkbox)`
  padding: ${Spacing[2]};
  max-width: ${MAX_VERIFICATION_TEMPLATE_FILTER_CHECKBOX_WIDTH}px;

  & > input:hover {
    ${hoverMixin}
  }

  label {
    font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
    color: ${Color.CoreSlate1000};
    // ellipse on long labels
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // on hover display the full template name wrapped
  > label:hover {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    max-width: none;
  }
`;

const TEMPLATE_DROPDOWN_HEIGHT = 325;

const StyledList = styled.div`
  padding: ${Spacing[2]};
  overflow-y: scroll;
  max-height: ${TEMPLATE_DROPDOWN_HEIGHT}px;
`;

const NameLabel = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${({ theme }) => theme.color.text.primary};
  padding-bottom: ${Spacing[1]};
`;

const IdLabel = styled(Paragraph).attrs(() => ({ size: 'sm' }))`
  color: ${({ theme }) => theme.color.text.secondary};
`;

const StyledHelpIcon = styled(HelpIcon)`
  position: relative;
  bottom: 0;
`;

const Label: FC<{ name: string; id: string; tooltipText?: string }> = ({
  name,
  id,
  tooltipText,
}) => (
  <>
    <NameLabel>
      {name}
      {tooltipText && <StyledHelpIcon>{tooltipText}</StyledHelpIcon>}
    </NameLabel>

    <IdLabel>{id}</IdLabel>
  </>
);

interface VerificationTemplateFilterProps {
  verificationType: VerificationType;
  verificationTemplates: { name: string; id: string; tooltipText?: string }[];
  handleVerificationTemplateFilterUpdate: (
    verificationType: VerificationType,
    allVTemplatesIdsForType: string[],
  ) => void;
  verificationTemplateFilterRules: string[];
}

const VerificationTemplateFilter: FC<VerificationTemplateFilterProps> = ({
  verificationType,
  verificationTemplates,
  handleVerificationTemplateFilterUpdate,
  verificationTemplateFilterRules,
}) => {
  // Use local state for "preselect all" functionality to avoid modifying filterRules, which would cause a flickering re-render.
  const [selectedVerificationTemplates, setSelectedVerificationTemplates] = useState<string[]>(
    verificationTemplates.map(({ id }) => id),
  );

  useEffect(() => {
    setSelectedVerificationTemplates(
      // verificationTemplateFilterRules is reset when the top level filters change (date, type, templates)
      // when verificationTemplateFilterRules is no longer in sync (is empty) with selectedVerificationTemplates,
      // we want to reset this filter by selecting all templates

      isEqual(verificationTemplateFilterRules, selectedVerificationTemplates)
        ? selectedVerificationTemplates
        : // In the event that we're going from some selected to none selected, we're going to leverage the full list
          verificationTemplateFilterRules.length === 0
          ? verificationTemplates.map(({ id }) => id)
          : verificationTemplateFilterRules,
    );
  }, [verificationTemplateFilterRules, verificationTemplates]);

  const handleVerificationTemplateFilterChange = (
    verificationType: VerificationType,
    selectedTemplateId: string,
  ) => {
    setSelectedVerificationTemplates((prevSelectedTemplates) => {
      const isIdAlreadySelected = prevSelectedTemplates.includes(selectedTemplateId);

      let updatedValue = isIdAlreadySelected
        ? prevSelectedTemplates.filter((id: string) => id !== selectedTemplateId)
        : [...prevSelectedTemplates, selectedTemplateId];

      // In the event that we eliminate all templates, we'll select all
      if (updatedValue.length === 0) {
        updatedValue = verificationTemplates.map(({ id }) => id);
      }

      handleVerificationTemplateFilterUpdate(verificationType, updatedValue);

      return updatedValue;
    });
  };

  const numSelected = selectedVerificationTemplates.length;
  const numSelectedTemplates = numSelected > 0 ? ` (${numSelected})` : '';

  const isCheckboxDisabled = verificationTemplates.length === 1;
  const buttonLabel = `Templates${numSelectedTemplates}`;

  return (
    <Popover
      contentProps={{
        noPadding: true,
        align: 'start',
      }}
      trigger={
        <StyledMetricsFilterButton
          data-test='verification-template-filter-button'
          type='button'
          variant='basic'
          width='auto'
          prefixIcon={<ListIcon />}
          prefixIconType='inherit'
        >
          {buttonLabel}
        </StyledMetricsFilterButton>
      }
    >
      <StyledList>
        {verificationTemplates.map(({ id, name, tooltipText }) => (
          <StyledCheckbox
            key={`verification-template-filter-checkbox-${id}`}
            label={<Label name={name} id={id} tooltipText={tooltipText} />}
            disabled={isCheckboxDisabled}
            checked={selectedVerificationTemplates.includes(id)}
            onClick={() => handleVerificationTemplateFilterChange(verificationType, id)}
          ></StyledCheckbox>
        ))}
      </StyledList>
    </Popover>
  );
};

export default VerificationTemplateFilter;
